import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mutationErrorHandler } from "am-tax-fe-core";

import { TOAST_DURATION } from "../../utils/constants";

import {
    getReportAsync,
    getReportsAsync,
    createReportAsync,
    updateReportAsync,
    deleteReportAsync,
    UpdateReport,
    CreateReport,
    getReportsByClientIdAsync,
} from "./reportApi";
import { queryKeys } from "../queryKeys";

export function useGetReports() {
    return useQuery({ ...queryKeys.reports.all, queryFn: getReportsAsync });
}

export function useGetReportsByClientId(clientId: string | undefined) {
    const toast = useToast();
    return useQuery({
        ...queryKeys.reports.byClientId(clientId || ""),
        queryFn: async () => {
            const reports = await getReportsByClientIdAsync(clientId || "")
                .then(reports => {
                    return reports;
                })
                .then(res => res)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the reports information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return reports;
        },
        enabled: !!clientId,
    });
}

export const useGetReport = (id: string | undefined) => {
    const toast = useToast();
    return useQuery({
        ...queryKeys.reports.byId(id || ""),
        queryFn: async () => {
            const report = await getReportAsync(id || "")
                .then(report => {
                    return report;
                })
                .then(res => res)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the report information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return report;
        },
        enabled: !!id,
    });
};

export function useUpdateReport() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: UpdateReport) => {
            return await updateReportAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.reports.all);
            toast({
                title: "Updated",
                description: "Report updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update report"),
    });
}

export function useCreateReport() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: CreateReport) => {
            return await createReportAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.reports.all);
            toast({
                title: "Created",
                description: "Report created successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to create report"),
    });
}

export function useDeleteReport() {
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: string) => {
            return await deleteReportAsync(args);
        },
        onSuccess: async () => {
            toast({
                title: "Deleted",
                description: "Report deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete report"),
    });
}
